import { useState, useCallback } from "react";
import {
    IPatientDetails,
    IPrescriberDetails,
    IMedicationDetails,
} from "../types/PrescriptionIngestionTypes";
import { PRESCRIBERS, MEDICATIONS } from "../config/PrescriptionDefaults";
import { IFieldErrors } from "../utils/ValidationUtils";
import {
    createEmptyPatientDetails,
    getInitialMedication,
} from "../utils/PrescriptionHelpers";

const DEFAULT_VALUES = {
    PLACE_OF_SERVICE: "10",
    SELECTED_PRESCRIBER: "dr_kulkarni",
} as const;

/**
 * Hook to manage prescription form state
 *
 * Handles:
 * - Patient details
 * - Prescriber selection
 * - Medication selection
 * - Place of service
 * - Form validation errors
 */
export const usePrescriptionForm = (initialMedicationKey?: string) => {
    const initialMedication =
        initialMedicationKey && MEDICATIONS[initialMedicationKey]
            ? { key: initialMedicationKey, details: MEDICATIONS[initialMedicationKey] }
            : getInitialMedication();

    const [patientDetails, setPatientDetails] = useState<IPatientDetails>(
        createEmptyPatientDetails()
    );
    const [prescriberDetails, setPrescriberDetails] = useState<IPrescriberDetails>(
        PRESCRIBERS[DEFAULT_VALUES.SELECTED_PRESCRIBER]
    );
    const [medicationDetails, setMedicationDetails] = useState<IMedicationDetails>(
        initialMedication.details
    );
    const [selectedPrescriber, setSelectedPrescriber] = useState<string>(
        DEFAULT_VALUES.SELECTED_PRESCRIBER
    );
    const [selectedMedication, setSelectedMedication] = useState<string>(
        initialMedication.key
    );
    const [placeOfService, setPlaceOfService] = useState<string>(
        DEFAULT_VALUES.PLACE_OF_SERVICE
    );
    const [prescriberOrderNumber, setPrescriberOrderNumber] = useState<string>("");
    const [fieldErrors, setFieldErrors] = useState<IFieldErrors>({});

    /**
     * Update a single patient detail field
     */
    const setPatientField = useCallback((field: keyof IPatientDetails, value: string) => {
        setPatientDetails((prev) => ({
            ...prev,
            [field]: value,
        }));
        // Clear error for this field
        setFieldErrors((prev) => ({
            ...prev,
            [field]: undefined,
        }));
    }, []);

    /**
     * Update all patient details at once
     */
    const updatePatientDetails = useCallback((details: IPatientDetails) => {
        setPatientDetails(details);
        setFieldErrors({});
    }, []);

    /**
     * Handle prescriber selection change
     */
    const handlePrescriberChange = useCallback((prescriberKey: string) => {
        const prescriber = PRESCRIBERS[prescriberKey];
        if (prescriber) {
            setSelectedPrescriber(prescriberKey);
            setPrescriberDetails(prescriber);
        }
    }, []);

    /**
     * Handle medication selection change
     */
    const handleMedicationChange = useCallback((medicationKey: string) => {
        const medication = MEDICATIONS[medicationKey];
        if (medication) {
            setSelectedMedication(medicationKey);
            setMedicationDetails(medication);
        }
    }, []);

    /**
     * Handle individual medication field changes
     */
    const handleMedicationFieldChange = useCallback((field: keyof IMedicationDetails, value: any) => {
        setMedicationDetails((prev) => ({
            ...prev,
            [field]: value,
        }));
        
        // Clear error for this field
        setFieldErrors((prev) => ({
            ...prev,
            [field]: undefined,
        }));
    }, []);

    /**
     * Handle place of service change
     */
    const handlePlaceOfServiceChange = useCallback((value: string) => {
        setPlaceOfService(value);
    }, []);

    /**
     * Set field errors (typically from validation)
     */
    const updateFieldErrors = useCallback((errors: IFieldErrors) => {
        setFieldErrors(errors);
    }, []);

    /**
     * Reset all form fields to defaults
     */
    const resetForm = useCallback(() => {
        const newInitialMedication =
            initialMedicationKey && MEDICATIONS[initialMedicationKey]
                ? { key: initialMedicationKey, details: MEDICATIONS[initialMedicationKey] }
                : getInitialMedication();
        setPatientDetails(createEmptyPatientDetails());
        setPrescriberDetails(PRESCRIBERS[DEFAULT_VALUES.SELECTED_PRESCRIBER]);
        setMedicationDetails(newInitialMedication.details);
        setSelectedPrescriber(DEFAULT_VALUES.SELECTED_PRESCRIBER);
        setSelectedMedication(newInitialMedication.key);
        setPlaceOfService(DEFAULT_VALUES.PLACE_OF_SERVICE);
        setPrescriberOrderNumber("");
        setFieldErrors({});
    }, [initialMedicationKey]);

    return {
        // State
        patientDetails,
        prescriberDetails,
        medicationDetails,
        selectedPrescriber,
        selectedMedication,
        placeOfService,
        prescriberOrderNumber,
        fieldErrors,

        // Setters
        setPatientField,
        updatePatientDetails,
        handlePrescriberChange,
        handleMedicationChange,
        handleMedicationFieldChange,
        handlePlaceOfServiceChange,
        setPrescriberOrderNumber,
        updateFieldErrors,
        resetForm,
    };
};
